import React, { useEffect, Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { chunkArray, generatePDF, highlightAll } from "../../utils";
import { NumericFormat } from "react-number-format";
import { Box, Card, CardContent, Typography, Grid, TextField, Divider, InputAdornment, Tooltip, Button } from "@mui/material";
import { updateValue, selectTotalValues, selectTotalById, updateYear } from "./totalsSlice";
import { selectExpenseTotalByType } from "./expensesSlice";
import { Expenses } from "./Expenses";

export const CostPer = () => {
	const dispatch = useDispatch();
	const totals = chunkArray(useSelector(selectTotalValues), 2);
	const fmTotal = useSelector(state => selectExpenseTotalByType(state, {type: "mixed", field: "fixed"}));
	const faTotal = useSelector(state => selectExpenseTotalByType(state, {type: "fixed", field: "amount"}));
	const vmTotal = useSelector(state => selectExpenseTotalByType(state, {type: "variable", field: "marginal"}));
	const mmTotal = useSelector(state => selectExpenseTotalByType(state, {type: "mixed", field: "marginal"}));
	const fixedCost = useSelector(state => selectTotalById(state, 2)).value;
	const adr = useSelector(state => selectTotalById(state, 1)).value;
	const marginalCost = useSelector(state => selectTotalById(state, 3)).value;
	const year = useSelector(state => state.costPer.totals.year);
	const expenses = [
		{label: "Fully Variable Expenses", type: "variable", xs: 6, headers: [
			{label: "Expense", xs: 6},
			{label: `${year} Expense Amount`, xs: 3},
			{label: "Marginal Cost Per Room", xs: 3}
		]},
		{label: "Fully Fixed Expenses", type: "fixed", xs: 6, headers: [
			{label: "Expense", xs: 8},
			{label: `${year} Expense Amount`, xs: 4}
		]},
		{label: "Mixed Expenses", type: "mixed", xs: 12, headers: [
			{label: "Expense", xs: 4},
			{label: `${year} Expense Amount`, xs: 2},
			{label: "% Variable", xs: 2},
			{label: "Marginal Cost Per Room", xs: 2},
			{label: "Fixed Cost Per Room", xs: 2}
		]}
	];

	const onChange = (e, id) => {
		dispatch(updateValue({id, changes: {value: e.target.value === "" ? 0.00 : parseFloat(e.target.value.replace(/,/g, ""))}}));
	};

	const handleYearChange = (e) => {
		dispatch(updateYear(e.target.value));
	};

	const print = () => {
		setTimeout(() => {
			generatePDF();
		}, 800);
	};

	useEffect(() => {
		dispatch(updateValue({id: 2, changes: {value: fmTotal + faTotal}}));
	}, [fmTotal, faTotal]);

	useEffect(() => {
		dispatch(updateValue({id: 3, changes: {value: vmTotal + mmTotal}}));
	}, [vmTotal, mmTotal]);

	useEffect(() => {
		dispatch(updateValue({id: 4, changes: {value: fixedCost / (adr - marginalCost)}}));
	}, [fixedCost, adr, marginalCost]);

	return (
		<Box sx={{pt: 3}}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Card>
						<CardContent sx={{display: "flex", flexDirection: "column"}}>
							<Box sx={{display: "flex", alignItems: "center"}}>
								<Typography sx={{mr: 2}}>
									<b>Year:</b>
								</Typography>
								<NumericFormat
									decimalScale={0}
									customInput={TextField}
									value={year}
									variant="outlined"
									onChange={handleYearChange}
									inputProps={{maxLength: 4, onFocus: highlightAll}}
								/>
							</Box>
							<Divider sx={{my: 2}} />
							<Grid container spacing={0}>
								{totals.map((group, key) =>
									<Fragment key={`cost_per_group_${key}`}>
										<Grid key={`group_${key}`} container spacing={0} item xs={3}>
											{group.map(({ id, label, adornment, readOnly, value }, i) =>
												<Grid key={`item_${key}_${i}`} container spacing={0} sx={{display: "flex", alignItems: "center", mb: 2}}>
													<Grid item xs={6}>
														<Typography sx={{mr: 2}}><b>{label}:</b></Typography>
													</Grid>
													<Grid item xs={6}>
														<NumericFormat
															prefix={(!!readOnly && adornment === "$") ? "$ " : ""}
															displayType={!!readOnly ? "text" : "input"}
															fixedDecimalScale
															allowLeadingZeros
															thousandSeparator=","
															decimalScale={adornment === "$" ? 2 : 0}
															customInput={TextField}
															sx={{"& input": {textAlign: "end"}}}
															{...{value}}
															onChange={(e) => onChange(e, id)}
															variant="outlined"
															size="small"
															fullWidth
															InputProps={{
																startAdornment: !!adornment && <InputAdornment position="start">{adornment}</InputAdornment>
															}}
															inputProps={{readOnly, onFocus: !readOnly && highlightAll}}
														/>
													</Grid>
												</Grid>
											)}
										</Grid>
										{key < 2 && <Grid item xs={1} sx={{display: "flex", justifyContent: "center"}}>
											<Divider orientation="vertical"/>
										</Grid>}
									</Fragment>
								)}
							</Grid>
						</CardContent>
					</Card>
				</Grid>
				{expenses.map(({label, type, xs, headers}, key) =>
					<Grid key={`expense_${key}`} item {...{xs}}>
						<Expenses {...{label, type, headers}} />
					</Grid>
				)}
				<Grid item xs={12} sx={{pt: 3, width: "100%", display: "flex", justifyContent: "center"}}>
					<Button
						variant="contained"
						onClick={print}
						sx={{
							p: 3,
							cursor: "pointer",
							display: "flex", alignItems: "center", justifyContent: "center",
							boxShadow: "rgba(46, 125, 50, .2) 0px 0px 10px",
							width: "100%",
							borderRadius: 2
						}}
					>
						Print
					</Button>
				</Grid>
			</Grid>
		</Box>
	);
};